// ** React Imports
import { useRef, useState } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Button from '@mui/material/Button'
import Stepper from '@mui/material/Stepper'
import { styled } from '@mui/material/styles'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import MuiStep, { StepProps } from '@mui/material/Step'
import CardContent, { CardContentProps } from '@mui/material/CardContent'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Custom Components Imports
import StepperCustomDot from 'src/views/samples/create-sample/StepperCustomDot'

// ** Step Components
import StepSample from '@/views/samples/create-sample/StepSample'
import StepPatient from '@/views/samples/create-sample/StepClinical'
import StepClinical from '@/views/samples/create-sample/StepPatient'

// ** Styled Components
import StepperWrapper from 'src/@core/styles/mui/stepper'
import { useMutation } from 'react-query'
import {
  createReports,
  createSample,
  Sample
} from '@/@core/services/app.service'

import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Divider, FormHelperText } from '@mui/material'
import { useTranslation } from 'react-i18next'
import ReportType from './ReportType'
import WordReport from './WordReport'
import FormReport from './FormReport'
import DragReport from './DragReport'
import PositionedSnackbar from '@/@core/components/message'
import { exportAsDocx } from '@/@core/utils/exportAsWord'
import { formatDate, isWindow } from '@/@core/utils/utils'
import React from 'react'
import { useRouter } from 'next/router'
import WordReportNew from './WordReportNew'

const Step = styled(MuiStep)<StepProps>(({ theme }) => ({
  '&:not(:last-of-type)': {
    marginBottom: theme.spacing(4)
  },
  '& .MuiStepLabel-root': {
    padding: 0,
    cursor: 'pointer'
  }
}))

const StepperHeaderContainer = styled(CardContent)<CardContentProps>(
  ({ theme }) => ({
    minWidth: 200,
    borderRight: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('md')]: {
      borderRight: 0,
      borderBottom: `1px solid ${theme.palette.divider}`
    }
  })
)

const formSchema = yup.object().shape({
  name: yup.string().required(),
  templatePath: yup.string().required(),
  result: yup.string().required()
})

export const getVariantId = (row: any) => {
  return row.AnnotSV_ID
    ? `${row.AnnotSV_ID}_${row.variantsType}`
    : row.GangSTR_repcn && row.GangSTR_repci
    ? `${row.gene}_${row.chrom}_${row.start}_${row.end}_${row.variantsType}`
    : row.variant && row.variant.chrom && row.variant.start
    ? `${row.variant.chrom}_${row.variant.start}_${row.variant.end}_${row.variantsType}`
    : row.variant && row.variant.chrom && row.variant.pos
    ? `${row.variant.chrom}_${row.variant.pos}_${row.variantsType}`
    : row.variant_id
    ? `${row.variant_id}_${row.variantsType}`
    : row.alt && row.ref
    ? `${row.chrom}_${row.pos}_${row.ref}_${row.alt}_${row.gene}_${row.variantsType}`
    : `${row.chr_pos_ref_alt_gene}_${row.variantsType}`
}

const wordSchema = yup.object().shape({})

const CreateReportWizard = (props: any) => {
  const { t } = useTranslation()
  const typeList = [
    {
      isSelected: false,
      value: 'Word',
      title: 'Word',
      content: t('Upload the template file for replacement.')
    },
    {
      isSelected: true,
      value: 'Form',
      title: 'Form',
      content: t('Select an existing report template for dynamic generation.')
    }

    // {
    //   isSelected: false,
    //   value: 'Drag',
    //   title: 'Drag',
    //   content:
    //     'Create a deal which offer uses some % off (i.e 5% OFF) on total.'
    // }
  ]
  const steps = [
    {
      title: t('upload'),
      icon: 'mdi:tag-outline',
      subtitle: t('Choose type of upload')
    },
    {
      title: t('report'),
      subtitle: t('Provide report details'),
      icon: 'mdi:clipboard-text-outline'
    }
  ]

  const router = useRouter()

  const { id } = router.query

  const type = router.asPath.includes('/samples') ? 'sample' : 'analysis'

  // ** States
  const [activeStep, setActiveStep] = useState<number>(0)
  const initialIconSelected: string = typeList.filter(
    (item) => item.isSelected
  )[typeList.filter((item) => item.isSelected).length - 1].value
  const [selectedRadio, setSelectedRadio] =
    useState<string>(initialIconSelected)
  const wordRef = React.useRef<any>()
  const formRef = React.useRef<any>()

  const defaultValues = {
    form: {
      name: ``,
      templatePath: '1',
      result: 'positive' as 'positive' | 'negative'
    },
    word: {
      name: '',
      templatePath: '21',
      result: 'positive' as 'positive' | 'negative'
    },
    drag: {}
  }
  const {
    control: formControl,
    setError: setFormError,
    handleSubmit: handleFormSubmit,
    formState: { errors: formErrors },
    reset,
    setValue
  } = useForm({
    defaultValues: defaultValues.form,
    mode: 'onBlur',
    resolver: yupResolver(formSchema)
  })

  const {
    control: wordControl,
    setError: setWrodError,
    handleSubmit: handleWordSubmit,
    formState: { errors: wordErrors }
  } = useForm({
    defaultValues: defaultValues.word,
    mode: 'onBlur',
    resolver: yupResolver(wordSchema)
  })

  // Handle Stepper
  const handleNext = () => {
    setActiveStep(activeStep + 1)
  }

  const handlePrev = () => {
    if (activeStep !== 0) {
      setActiveStep(activeStep - 1)
    }
  }

  const getStepContent = (step: number) => {
    const index = typeList.findIndex((items) => items.value === selectedRadio)

    switch (step) {
      case 0:
        return (
          <ReportType
            selectedRadio={selectedRadio}
            data={typeList}
            fn={setSelectedRadio}
          />
        )
      case 1:
        return getReport(index)
      default:
        return null
    }
  }

  const resetName = () => {
    setValue('name', `${props.sampleName}`)
  }

  const getReport = (step: number) => {
    switch (step) {
      case 0:
        // return <WordReport ref={wordRef} />
        return (
          <WordReportNew
            ref={formRef}
            control={formControl}
            errors={formErrors}
            reset={resetName}
            getReportsVariants={props.getReportsVariants}
          />
        )
      case 1:
        return (
          <FormReport
            ref={formRef}
            control={formControl}
            errors={formErrors}
            getReportsVariants={props.getReportsVariants}
          />
        )
      case 2:
        return <DragReport />
      default:
        return null
    }
  }

  const renderContent = () => {
    return getStepContent(activeStep)
  }

  const renderFooter = () => {
    const stepCondition = activeStep === steps.length - 1

    return (
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          color="secondary"
          variant="outlined"
          onClick={handlePrev}
          disabled={activeStep === 0}
          startIcon={<Icon icon="mdi:arrow-left" />}
        >
          {t('Previous')}
        </Button>

        {stepCondition ? (
          <Button
            variant="contained"
            color={'success'}
            {...(!stepCondition
              ? { endIcon: <Icon icon="mdi:arrow-right" /> }
              : {})}
            type="submit"
          >
            {t('Submit')}
          </Button>
        ) : (
          <Button
            variant="contained"
            color={'primary'}
            {...(!stepCondition
              ? { endIcon: <Icon icon="mdi:arrow-right" /> }
              : {})}
            onClick={() => {
              handleNext()
            }}
          >
            {stepCondition ? t('Submit') : t('Next')}
          </Button>
        )}
      </Box>
    )
  }

  const createReportsMutation = useMutation(createReports, {
    onSuccess: (data) => {
      if (data && data.id) props?.fn && props?.fn(data.id)
    }
  })

  // React.useEffect(() => {
  //   if (props.patientName && props.sampleName) {
  //     setValue('name', `${props.patientName} ${props.sampleName}`)
  //   }
  // }, [props.patientName, props.sampleName])

  return (
    <Card>
      <CardContent>
        <StepperHeaderContainer>
          <StepperWrapper>
            <Stepper
              activeStep={activeStep}
              orientation={isWindow() ? 'horizontal' : 'vertical'}
              sx={{
                '& .MuiStepConnector-line': isWindow()
                  ? {}
                  : {
                      border: 'none',
                      borderLeft: '3px solid',
                      borderLeftColor: 'primary.main'
                    }
              }}
            >
              {steps.map((step, index) => {
                return (
                  <Step key={index} onClick={() => setActiveStep(index)}>
                    <StepLabel StepIconComponent={StepperCustomDot}>
                      <div className="step-label">
                        <Typography className="step-number">{`0${
                          index + 1
                        }`}</Typography>

                        <div>
                          <Typography className="step-title">
                            {step.title}
                          </Typography>

                          <Typography className="step-subtitle">
                            {step.subtitle}
                          </Typography>
                        </div>
                      </div>
                    </StepLabel>
                  </Step>
                )
              })}
            </Stepper>
          </StepperWrapper>
        </StepperHeaderContainer>
      </CardContent>

      <Divider sx={{ m: '0 !important' }} />

      <Box
        sx={{
          maxHeight: '56vh',
          overflow: 'auto'
        }}
      >
        <CardContent>
          <form
            onSubmit={handleFormSubmit((data) => {
              const user = JSON.parse(localStorage.getItem('user') || '{}')

              // const objectId =
              //   formRef.current.rowSelection.length &&
              //   formRef.current.rowSelection[0].ReportVariantsId
              const selectRow = formRef.current.rowSelection
              const setValue = formRef.current.setValue
              const thalassemia = formRef.current.thalassemia
              const isRoh = formRef.current.isRoh
              const isACMGSF = formRef.current.isACMGSF
              const newOMIMData = selectRow
                .map((item: any) => item.newOMIMData)
                .flat()
                .map((item: any) => `${item.keyid}==${item.isChecked}`)
              const oldOMIMData = selectRow
                .map((item: any) => item.oldOMIMData)
                .flat()
                .map((item: any) => `${item.keyid}==${item.isChecked}`)
              let metadata: any = JSON.stringify(
                selectRow.map((item: any) => getVariantId(item))
              )

              if (data.templatePath === '4') {
                metadata = JSON.stringify({
                  json: selectRow.map((item: any) => getVariantId(item)),
                  set: setValue,
                  newOMIMData: newOMIMData,
                  oldOMIMData: oldOMIMData
                })
              } else if (
                data.templatePath === '12' ||
                data.templatePath === '13'
              ) {
                metadata = JSON.stringify({
                  json: selectRow.map((item: any) => getVariantId(item)),
                  thalassemia: thalassemia,
                  newOMIMData: newOMIMData,
                  oldOMIMData: oldOMIMData
                })
              } else if (
                data.templatePath === '22' ||
                data.templatePath === '6' ||
                data.templatePath === '7'
              ) {
                metadata = JSON.stringify({
                  json: selectRow.map((item: any) => getVariantId(item)),
                  isRoh: isRoh,
                  isACMGSF: isACMGSF,
                  sanger: selectRow.map((item: any) => {
                    return {
                      fileName: item.fileName,
                      base: item.base,
                      keyId: item.keyId
                    }
                  }),
                  newOMIMData: newOMIMData,
                  oldOMIMData: oldOMIMData
                })
              } else if (
                data.templatePath === '8' ||
                data.templatePath === '9' ||
                data.templatePath === '21'
              ) {
                metadata = JSON.stringify({
                  json: selectRow.map((item: any) => getVariantId(item)),
                  isACMGSF: isACMGSF,
                  sanger: selectRow.map((item: any) => {
                    return {
                      fileName: item.fileName,
                      base: item.base,
                      keyId: item.keyId
                    }
                  }),
                  newOMIMData: newOMIMData,
                  oldOMIMData: oldOMIMData
                })
              } else if (
                data.templatePath === '24' ||
                data.templatePath === '25' ||
                data.templatePath === '26'
              ) {
                metadata = JSON.stringify({
                  isRoh: isRoh,
                  json: selectRow.map((item: any) => getVariantId(item)),
                  newOMIMData: newOMIMData,
                  oldOMIMData: oldOMIMData
                })
              }

              //

              const params = {
                ...data,
                createdAt: new Date(),
                objectType: type,
                language: 'zh',
                updatedAt: null,
                templateId: null,
                userId: user.id,
                objectId: id as unknown as number,
                metadata
              } as any

              if (data.templatePath === '9' && data.result === 'negative') {
                params.templatePath = '10'
              }

              createReportsMutation.mutateAsync(params)
            })}
          >
            {renderContent()}

            {renderFooter()}
          </form>
        </CardContent>
      </Box>
    </Card>
  )
}

export default CreateReportWizard
